import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    InputAdornment,
    FormControlLabel,
    Link,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Link as LinkIcon,
    ExpandMore as ExpandMoreIcon,
    Lock as LockIcon,
    Tune as TuneIcon,
} from '@mui/icons-material';
import type { IProject } from '../../../types/global/helper';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    IInfluencer,
    IShippingAddress,
    IUnAvailabelDates,
    IDocument,
    ICategory,
} from '../../../redux/buzzbridge/types';
import { countries } from '../../../utils/countriesList';
import PictureUploader from '../../PictureUploader/PictureUploader';
import { CustomHeaderWithStatus } from '../../EsForm/CustomHeaderWithStatus';
import { ShippingAddress } from '../../ShippingAddress/ShippingAddress';
import { LANGUAGES } from '../../FindTalentsContainer/filterEnums';
import { CustomizedSwitches } from '../../Switch/Switch';
import { DropdownSelect } from '../../DropdownSelect/DropdownSelect';
import { CustomDatePicker } from '../../DatePicker/CustomDatePicker';
import { EditProfileConnectedPlatformCards } from './EditProfileConnectedPlatformCards';
import { EditProfileRateCardTab } from './EditProfileRateCardTab';
import { EditProfileMediaExtras, initialMediaExtrasState, type GalleryUploadItem } from './EditProfileMediaExtras';
import { EditProfileConfigTab } from './EditProfileConfigTab';
import {
    isEphemeralSocialAvatarUrl,
    PERSIST_SOCIAL_AVATAR_FLAG,
    resolveInfluencerDisplayAvatarUrl,
    shouldAttemptSocialAvatarPersist,
} from '../../../utils/influencerSocialAvatar';
import {
    NICHE_MAIN_ORDER,
    NICHE_SUBS_BY_MAIN,
    findMainCategoryForSub,
} from '../../../pages/BrandDashboard/ugcCampaignCategorySchema';
import { useContentCategories } from '../../../hooks/useContentCategories';
import { findMainCategoryForSubFromCatalog } from '../../../utils/contentCategoriesApi';

interface EditProfileModalProps {
    open: boolean;
    onClose: () => void;
    influencer: IInfluencer;
    onSave: (data: Partial<IInfluencer>) => void;
    /** Refresh influencer after OAuth connect so social avatars appear in preview. */
    onPlatformConnected?: () => void;
    /** Tab to show when the dialog opens. Social Platforms is 5. */
    initialTab?: number;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`edit-profile-tabpanel-${index}`}
            aria-labelledby={`edit-profile-tab-${index}`}
            {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

function getAllExceptParentAndChildren(parent: string, subsByMain: Record<string, string[]>): Record<string, string[]> {
    const result: Record<string, string[]> = {};

    for (const [key, value] of Object.entries(subsByMain)) {
        if (key !== parent) {
            result[key] = [...value];
        }
    }

    return result;
}

// Extended document type to handle both existing documents and newly uploaded files
interface ExtendedDocument extends IDocument {
    size?: number;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
    open,
    onClose,
    influencer,
    onSave,
    onPlatformConnected,
    initialTab = 0,
}) => {
    const { themeStyle } = useThemeContext();
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        if (open) setActiveTab(initialTab);
    }, [open, initialTab]);
    const { catalog } = useContentCategories();
    const mainCategory = catalog.mains.length ? catalog.mains : [...NICHE_MAIN_ORDER];
    const [subCategoriesMapped, setSubCategories] = useState<Record<string, string[]>>(() =>
        Object.fromEntries(NICHE_MAIN_ORDER.map(main => [main, [...NICHE_SUBS_BY_MAIN[main]]])),
    );

    useEffect(() => {
        if (!catalog.mains.length) return;
        setSubCategories(Object.fromEntries(catalog.mains.map(main => [main, [...(catalog.subsByMain[main] || [])]])));
    }, [catalog]);
    const [formData, setFormData] = useState<Partial<IInfluencer>>({
        ...influencer,
    });

    // Store original data for comparison
    const [originalData] = useState<Partial<IInfluencer>>({
        ...influencer,
    });

    // Profile picture state
    const [profilePicture, setProfilePicture] = useState<string | undefined>(influencer.profile_picture);
    const [originalProfilePicture] = useState<string | undefined>(influencer.profile_picture);

    // Shipping location state
    const [shippingLocation, setShippingLocation] = useState<IShippingAddress | undefined>(influencer.shipping_address);
    const [originalShippingLocation] = useState<IShippingAddress | undefined>(influencer.shipping_address);

    // Document state - use ExtendedDocument type
    const [uploadedDocuments, setUploadedDocuments] = useState<ExtendedDocument[]>(
        (influencer.documents || []) as ExtendedDocument[],
    );
    const [originalDocuments] = useState<ExtendedDocument[]>((influencer.documents || []) as ExtendedDocument[]);

    // Portfolio state
    const [newPortfolioLink, setNewPortfolioLink] = useState<string>('');

    const mediaExtrasSeed = initialMediaExtrasState(influencer);
    const [portfolioProjects, setPortfolioProjects] = useState<IProject[]>(mediaExtrasSeed.projects);
    const [originalPortfolioProjects] = useState<IProject[]>(mediaExtrasSeed.projects);
    const [galleryUrls, setGalleryUrls] = useState<string[]>(mediaExtrasSeed.galleryUrls);
    const [originalGalleryUrls] = useState<string[]>(mediaExtrasSeed.galleryUrls);
    const [pendingGalleryUploads, setPendingGalleryUploads] = useState<GalleryUploadItem[]>([]);

    // Category selection state
    const [selectedMainCategories, setSelectedMainCategories] = useState<string[]>(
        influencer.categories?.map(cat => cat.main) || [],
    );
    const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
        influencer.categories?.filter(cat => cat.sub).map(cat => cat.sub!) || [],
    );
    const [originalCategories] = useState<ICategory[]>(influencer.categories || []);

    // Unavailable date state
    const [newUnavailableDate, setNewUnavailableDate] = useState<IUnAvailabelDates>({
        from: '',
        to: '',
        reason: '',
    });

    useEffect(() => {
        // Update form data when influencer prop changes
        setFormData(influencer);
        setProfilePicture(influencer.profile_picture);
        setShippingLocation(influencer.shipping_address);
        setUploadedDocuments((influencer.documents || []) as ExtendedDocument[]);
        setSelectedMainCategories(influencer.categories?.map(cat => cat.main) || []);
        setSelectedSubCategories(influencer.categories?.filter(cat => cat.sub).map(cat => cat.sub!) || []);
        const extras = initialMediaExtrasState(influencer);
        setPortfolioProjects(extras.projects);
        setGalleryUrls(extras.galleryUrls);
        setPendingGalleryUploads([]);
    }, [influencer]);

    // Helper function to check if values are different
    const hasChanged = (current: any, original: any): boolean => {
        if (Array.isArray(current) && Array.isArray(original)) {
            if (current.length !== original.length) return true;
            return current.some((item, index) => JSON.stringify(item) !== JSON.stringify(original[index]));
        }
        return JSON.stringify(current) !== JSON.stringify(original);
    };

    // Helper function to get only changed fields
    const getChangedFields = (current: any, original: any): any => {
        const changes: any = {};

        Object.keys(current).forEach(key => {
            if (hasChanged(current[key], original[key])) {
                changes[key] = current[key];
            }
        });

        return changes;
    };

    // Helper function to check if there are any changes
    const projectIds = (items: IProject[]) =>
        items
            .map(p => p._id)
            .filter(Boolean)
            .sort()
            .join(',');

    const persistedGalleryUrls = (urls: string[]) => urls.filter(u => !u.startsWith('blob:'));

    const displayAvatarUrl = resolveInfluencerDisplayAvatarUrl(influencer);
    const needsSocialAvatarPersist = shouldAttemptSocialAvatarPersist(influencer, {
        hasOwnUpload: Boolean((formData as { rawProfile?: File[] }).rawProfile?.length),
    });

    const hasAnyChanges = (): boolean => {
        if (needsSocialAvatarPersist) return true;
        const currentData = {
            ...formData,
            profile_picture: profilePicture,
            shipping_address: shippingLocation,
            documents: uploadedDocuments,
            projects: projectIds(portfolioProjects),
            gallery: persistedGalleryUrls(galleryUrls),
            categories: [
                ...selectedSubCategories.map(sub => {
                    const parent = findMainCategoryForSubFromCatalog(catalog, sub) || findMainCategoryForSub(sub);
                    const obj = { main: '', sub };
                    if (!!parent) {
                        obj.main = parent;
                    }
                    return obj;
                }),
            ],
        };

        const originalDataForComparison = {
            ...originalData,
            profile_picture: originalProfilePicture,
            shipping_address: originalShippingLocation,
            documents: originalDocuments,
            projects: projectIds(originalPortfolioProjects),
            gallery: originalGalleryUrls,
            categories: originalCategories,
        };

        const changedFields = getChangedFields(currentData, originalDataForComparison);
        return Object.keys(changedFields).length > 0 || pendingGalleryUploads.length > 0;
    };

    // Collaboration types and payment methods
    const COLLABORATION_TYPES = [
        'paid',
        'gifted',
        'product exchange',
        'commission based',
        'affiliate',
        'sponsored',
        'brand ambassador',
        'influencer marketing',
        'content creation',
        'event appearance',
    ];

    const PAYMENT_METHODS = [
        'paypal',
        'wire transfer',
        'cryptocurrency',
        'stock options',
        'bank transfer',
        'stripe',
        'venmo',
        'cash app',
        'zelle',
        'check',
    ];

    const handleInputChange = (field: keyof IInfluencer, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSelectChange = (field: keyof IInfluencer) => (event: SelectChangeEvent) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleArrayFieldChange = (field: keyof IInfluencer, value: string[]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRemoveArrayItem = (field: keyof IInfluencer, itemToRemove: string) => {
        const currentArray = (formData[field] as string[]) || [];
        handleArrayFieldChange(
            field,
            currentArray.filter(item => item !== itemToRemove),
        );
    };

    const handleProfilePictureChange = (image: any, rawFile?: any) => {
        setProfilePicture(image);
        setFormData(prev => ({
            ...prev,
            profile_picture: image,
            rawProfile: image ? rawFile : undefined,
        }));
    };

    const handleLocationSelect = (address?: IShippingAddress) => {
        if (address) {
            setShippingLocation(address);
            setFormData(prev => ({
                ...prev,
                shipping_address: address,
            }));
        }
    };

    const handleAddUnavailableDate = () => {
        if (newUnavailableDate.from && newUnavailableDate.to && newUnavailableDate.reason) {
            const currentDates = (formData.unavailable_dates as any) || [];
            const datesArray = Array.isArray(currentDates) ? currentDates : [];
            handleInputChange('unavailable_dates', [...datesArray, newUnavailableDate]);
            setNewUnavailableDate({
                from: '',
                to: '',
                reason: '',
            });
        }
    };

    const handleRemoveUnavailableDate = (index: number) => {
        const currentDates = (formData.unavailable_dates as any) || [];
        const datesArray = Array.isArray(currentDates) ? currentDates : [];
        const updatedDates = datesArray.filter((_: any, i: number) => i !== index);
        handleInputChange('unavailable_dates', updatedDates);
    };

    const handleAddPortfolioLink = () => {
        if (newPortfolioLink.trim()) {
            const currentPortfolio = formData.portfolio || [];
            if (!currentPortfolio.includes(newPortfolioLink.trim())) {
                handleArrayFieldChange('portfolio', [...currentPortfolio, newPortfolioLink.trim()]);
                setNewPortfolioLink('');
            }
        }
    };

    const handleRemovePortfolioLink = (linkToRemove: string) => {
        const currentPortfolio = formData.portfolio || [];
        handleArrayFieldChange(
            'portfolio',
            currentPortfolio.filter(link => link !== linkToRemove),
        );
    };

    const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles: ExtendedDocument[] = Array.from(files).map(file => ({
                url: URL.createObjectURL(file),
                name: file.name,
                format: file.type,
                size: file.size, // Include size for newly uploaded files
                file: file,
            }));

            setUploadedDocuments(prev => {
                const updatedDocs = [...prev, ...newFiles];
                setFormData(prevData => ({
                    ...prevData,
                    documents: updatedDocs,
                }));
                return updatedDocs;
            });
        }
    };

    const handleRemoveDocument = (index: number) => {
        setUploadedDocuments(prev => {
            const updatedDocs = prev.filter((_, i) => i !== index);
            setFormData(prevData => ({
                ...prevData,
                documents: updatedDocs,
            }));
            return updatedDocs;
        });
    };

    const handleSave = () => {
        // Create categories array from main and sub categories
        const categories: ICategory[] = [
            ...selectedSubCategories.map(sub => {
                const parent = findMainCategoryForSubFromCatalog(catalog, sub) || findMainCategoryForSub(sub);
                const obj = { main: '', sub };
                if (!!parent) {
                    obj.main = parent;
                }
                return obj;
            }),
        ];

        const currentData = {
            ...formData,
            profile_picture: profilePicture,
            shipping_address: shippingLocation,
            documents: uploadedDocuments,
            projects: portfolioProjects.map(p => p._id).filter(Boolean),
            gallery: persistedGalleryUrls(galleryUrls),
            categories: categories,
        };

        const changedFields = getChangedFields(currentData, {
            ...originalData,
            profile_picture: originalProfilePicture,
            shipping_address: originalShippingLocation,
            documents: originalDocuments,
            projects: originalPortfolioProjects.map(p => p._id).filter(Boolean),
            gallery: originalGalleryUrls,
            categories: originalCategories,
        });

        if (pendingGalleryUploads.length > 0) {
            changedFields.rawGallery = pendingGalleryUploads.map(p => p.file);
        }

        const hasOwnUpload = Boolean((formData as { rawProfile?: File[] }).rawProfile?.length);
        if (changedFields.profile_picture && isEphemeralSocialAvatarUrl(changedFields.profile_picture, influencer)) {
            delete changedFields.profile_picture;
        }
        if (shouldAttemptSocialAvatarPersist(influencer, { hasOwnUpload })) {
            changedFields[PERSIST_SOCIAL_AVATAR_FLAG] = true;
        }

        onSave(changedFields);
    };

    const commonTextFieldProps = {
        sx: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme[themeStyle].form_divider },
                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                '&.Mui-focused fieldset': { borderColor: theme[themeStyle].highlight },
            },
            '& .MuiInputLabel-root': {
                color: theme[themeStyle].form_label,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: theme[themeStyle].highlight },
            '& .MuiInputBase-input': {
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& ::placeholder': {
                color: theme[themeStyle].placeholderGray,
                fontFamily: FONT_ENUM.REGULAR_HEADER,
            },
        },
    };

    const commonSelectProps = {
        sx: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme[themeStyle].form_divider },
                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                '&.Mui-focused fieldset': { borderColor: theme[themeStyle].highlight },
            },
            '& .MuiInputLabel-root': {
                color: theme[themeStyle].form_label,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: theme[themeStyle].highlight },
            '& .MuiSelect-select': {
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
        },
    };

    const muiTheme = useTheme();
    const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));

    const editBioNavItems: { index: number; label: string }[] = [
        { index: 0, label: 'Basic Info' },
        { index: 1, label: 'Categories' },
        { index: 2, label: 'Languages' },
        { index: 3, label: 'Preferences' },
        { index: 4, label: 'Media' },
        { index: 5, label: 'Social Platforms' },
        { index: 6, label: 'Rate Card' },
        { index: 7, label: 'Config' },
    ];

    /** Frosted chrome panel — matches ContentCreator floating dialogs */
    const chromePaperSurface =
        themeStyle === 'dark'
            ? {
                  backgroundColor: '#131313',
                  border: '1px solid rgba(72, 72, 71, 0.45)',
                  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45)',
              }
            : {
                  backgroundColor: 'rgba(255, 255, 255, 0.78)',
                  backdropFilter: 'blur(22px) saturate(165%)',
                  WebkitBackdropFilter: 'blur(22px) saturate(165%)',
                  border: `1px solid rgba(0, 0, 0, 0.08)`,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.85)',
              };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            fullScreen={!isMdUp}
            BackdropProps={{
                sx: {
                    backgroundColor: themeStyle === 'dark' ? 'rgba(8, 8, 8, 0.38)' : 'rgba(255, 255, 255, 0.48)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                },
            }}
            sx={{
                backgroundColor: 'transparent',
            }}
            PaperProps={{
                sx: {
                    ...chromePaperSurface,
                    color: theme[themeStyle].primary_white,
                    borderRadius: isMdUp ? 2 : 0,
                    maxWidth: isMdUp ? 860 : '100%',
                    width: '100%',
                    height: isMdUp ? 'min(90vh, 920px)' : '100%',
                    maxHeight: isMdUp ? '90vh' : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                    flexShrink: 0,
                    bgcolor: themeStyle === 'dark' ? '#1a1919' : 'transparent',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(255, 201, 101, 0.2)',
                            color: '#ffc965',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                        <TuneIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: 16,
                                color: theme[themeStyle].primary_white,
                                lineHeight: 1.2,
                            }}>
                            Creator Profile & Rate Card
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: theme[themeStyle].form_label,
                                mt: 0.25,
                            }}>
                            Configure your public BuzzBridge discovery card
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        color: theme[themeStyle].form_label,
                        borderRadius: 1.5,
                        '&:hover': {
                            color: theme[themeStyle].primary_white,
                            backgroundColor: themeStyle === 'dark' ? '#262626' : theme[themeStyle].form_divider,
                        },
                    }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    flexShrink: 0,
                    overflowX: 'auto',
                    bgcolor: themeStyle === 'dark' ? '#201f1f' : theme[themeStyle].form_divider,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}>
                {editBioNavItems.map(({ index, label }) => {
                    const selected = activeTab === index;
                    return (
                        <Button
                            key={label}
                            onClick={() => setActiveTab(index)}
                            sx={{
                                flexShrink: 0,
                                px: 1.5,
                                py: 0.75,
                                minWidth: 0,
                                borderRadius: 1,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                fontWeight: 600,
                                color: selected ? '#ffc965' : theme[themeStyle].form_label,
                                bgcolor: selected
                                    ? themeStyle === 'dark'
                                        ? '#262626'
                                        : theme[themeStyle].primary_white
                                    : 'transparent',
                                '&:hover': {
                                    bgcolor: selected
                                        ? themeStyle === 'dark'
                                            ? '#262626'
                                            : theme[themeStyle].primary_white
                                        : 'transparent',
                                    color: selected ? '#ffc965' : theme[themeStyle].primary_white,
                                },
                            }}>
                            {label}
                        </Button>
                    );
                })}
            </Box>

            <DialogContent
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    pb: { xs: 10, md: 0 },
                }}>
                <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: { xs: 2, md: 3 } }}>
                        <TabPanel value={activeTab} index={0}>
                            <Stack spacing={3}>
                                {/* Profile Picture */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].primary_white,
                                            mb: 2,
                                            textAlign: 'center',
                                            width: '100%',
                                        }}>
                                        Profile Picture
                                    </Typography>
                                    <PictureUploader
                                        initialImageSrc={profilePicture || displayAvatarUrl}
                                        onChange={handleProfilePictureChange}
                                        onRemove={() => handleProfilePictureChange(undefined)}
                                        cropShape="round"
                                        aspect={1}
                                        size={160}
                                        sx={{ alignItems: 'center' }}
                                    />
                                </Box>

                                {/* Basic Information */}
                                <Grid
                                    container
                                    spacing={2}
                                    width={'100%'}
                                    sx={{
                                        '.MuiGrid-item': {
                                            px: 0.5,
                                        },
                                    }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Name"
                                            fullWidth
                                            value={formData.name || ''}
                                            onChange={e => handleInputChange('name', e.target.value)}
                                            {...commonTextFieldProps}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Date of Birth"
                                            fullWidth
                                            type="date"
                                            value={formData.date_of_birth || ''}
                                            onChange={e => handleInputChange('date_of_birth', e.target.value)}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            {...commonTextFieldProps}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        }}>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                '&.Mui-focused': { color: theme[themeStyle].highlight },
                                            }}>
                                            <InputLabel
                                                sx={{
                                                    color: theme[themeStyle].form_label,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                Country
                                            </InputLabel>
                                            <Select
                                                value={formData.country || ''}
                                                onChange={handleSelectChange('country')}
                                                label="Country"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray,
                                                            borderWidth: '1px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                            borderWidth: '1px',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                            borderWidth: '2px',
                                                        },
                                                    },
                                                    '& .MuiSelect-select': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: theme[themeStyle].primary_white,
                                                    },
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme[themeStyle].dropdown_black,
                                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                                            borderRadius: '8px',
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].primary_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}>
                                                {countries.map(country => (
                                                    <MenuItem key={country.code} value={country.name}>
                                                        {country.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        }}>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                },
                                                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                            }}>
                                            <InputLabel
                                                sx={{
                                                    color: theme[themeStyle].form_label,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '&.Mui-focused': { color: theme[themeStyle].highlight },
                                                }}>
                                                Nationality
                                            </InputLabel>
                                            <Select
                                                value={formData.nationality || ''}
                                                onChange={handleSelectChange('nationality')}
                                                label="Nationality"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].form_divider,
                                                            borderWidth: '1px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                            borderWidth: '1px',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                            borderWidth: '2px',
                                                        },
                                                    },
                                                    '& .MuiSelect-select': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: theme[themeStyle].primary_white,
                                                    },
                                                    ...commonSelectProps,
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme[themeStyle].dropdown_black,
                                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                                            borderRadius: '8px',
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].dropdown_text,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}>
                                                {countries.map(country => (
                                                    <MenuItem key={country.code} value={country.name}>
                                                        {country.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Bio"
                                            fullWidth
                                            multiline
                                            rows={4}
                                            value={formData.bio || ''}
                                            onChange={e => handleInputChange('bio', e.target.value)}
                                            {...commonTextFieldProps}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Shipping Location */}
                                <Box>
                                    <CustomHeaderWithStatus
                                        headerTitle="Shipping Location"
                                        themeStyle={themeStyle}
                                        field="shipping_address"
                                        state={formData}
                                        headerToolTip="This address will be used for shipping your products, so please make sure all details are correct and up to date."
                                        style={{
                                            marginBottom: '16px',
                                        }}
                                    />
                                    <ShippingAddress value={shippingLocation} onChange={handleLocationSelect} />
                                </Box>
                            </Stack>
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            <Stack spacing={3}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Content Categories
                                </Typography>

                                {/* Main Category Selection */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Main Categories (Max 3)
                                    </Typography>

                                    {/* Selected Main Categories Chips */}
                                    {selectedMainCategories.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            {selectedMainCategories.map((category, index) => (
                                                <Chip
                                                    key={index}
                                                    label={category}
                                                    onDelete={() => {
                                                        const updated = selectedMainCategories.filter(
                                                            (_, i) => i !== index,
                                                        );
                                                        setSelectedMainCategories(updated);
                                                    }}
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].black,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: theme[themeStyle].black,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                        }}>
                                        <InputLabel
                                            sx={{
                                                color: theme[themeStyle].form_label,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '&.Mui-focused': { color: theme[themeStyle].highlight },
                                            }}>
                                            Select Main Categories
                                        </InputLabel>
                                        <Select
                                            multiple
                                            value={selectedMainCategories}
                                            onChange={event => {
                                                const value = event.target.value as string[];
                                                // Limit to 3 selections
                                                const limitedValue = value.slice(0, 3);
                                                setSelectedMainCategories(limitedValue);
                                                // Clear sub categories when main categories change
                                                setSelectedSubCategories([]);
                                            }}
                                            label="Select Main Categories"
                                            renderValue={selected => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(selected as string[]).map(value => (
                                                        <Chip
                                                            key={value}
                                                            label={value}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].black,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].form_divider,
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiSelect-select': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme[themeStyle].dropdown_black,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: '8px',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme[themeStyle].primary_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}>
                                            {mainCategory.map(category => (
                                                <MenuItem key={category} value={category}>
                                                    {category}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Sub Category Selection */}
                                {selectedMainCategories.length > 0 && (
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].form_label,
                                                mb: 1,
                                            }}>
                                            Sub Categories (Max 3)
                                        </Typography>

                                        {/* Selected Sub Categories Chips */}
                                        {selectedSubCategories.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                {selectedSubCategories.map((category, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={category}
                                                        onDelete={() => {
                                                            const updated = selectedSubCategories.filter(
                                                                (_, i) => i !== index,
                                                            );
                                                            setSelectedSubCategories(updated);
                                                        }}
                                                        sx={{
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].black,
                                                            '& .MuiChip-deleteIcon': {
                                                                color: theme[themeStyle].black,
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}

                                        <FormControl
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                },
                                                '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                            }}>
                                            <InputLabel
                                                sx={{
                                                    color: theme[themeStyle].form_label,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '&.Mui-focused': { color: theme[themeStyle].highlight },
                                                }}>
                                                Select Sub Categories
                                            </InputLabel>
                                            <Select
                                                multiple
                                                value={selectedSubCategories}
                                                onChange={event => {
                                                    const value = event.target.value as string[];
                                                    // Limit to 3 selections
                                                    const limitedValue = value.slice(0, 3);
                                                    setSelectedSubCategories(limitedValue);
                                                    value.forEach(val => {
                                                        const parent =
                                                            findMainCategoryForSubFromCatalog(catalog, val) ||
                                                            findMainCategoryForSub(val);
                                                        if (!!parent) {
                                                            const children = getAllExceptParentAndChildren(
                                                                parent,
                                                                catalog.subsByMain,
                                                            );
                                                            setSubCategories(children);
                                                        }
                                                    });
                                                }}
                                                label="Select Sub Categories"
                                                renderValue={selected => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {(selected as string[]).map(value => (
                                                            <Chip
                                                                key={value}
                                                                label={value}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    color: theme[themeStyle].black,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].form_divider,
                                                            borderWidth: '1px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                            borderWidth: '1px',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                            borderWidth: '2px',
                                                        },
                                                    },
                                                    '& .MuiSelect-select': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: theme[themeStyle].primary_white,
                                                    },
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme[themeStyle].dropdown_black,
                                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                                            borderRadius: '8px',
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].primary_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}>
                                                {(() => {
                                                    const subCategories = selectedMainCategories
                                                        .map(main => subCategoriesMapped[main] || [])
                                                        .flat();

                                                    return subCategories.map(category => (
                                                        <MenuItem key={category} value={category}>
                                                            {category}
                                                        </MenuItem>
                                                    ));
                                                })()}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}

                                {/* Add Category Button */}
                                {selectedMainCategories.length > 0 && (
                                    <Box>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const newCategories = selectedMainCategories.map(main => ({ main }));
                                                const currentCategories = formData.categories || [];
                                                const categoryExists = newCategories.some(newCat =>
                                                    currentCategories.some(
                                                        existingCat => existingCat.main === newCat.main,
                                                    ),
                                                );

                                                if (!categoryExists) {
                                                    handleInputChange('categories', [
                                                        ...currentCategories,
                                                        ...newCategories,
                                                    ]);
                                                    // Clear selections after adding
                                                    setSelectedMainCategories([]);
                                                    setSelectedSubCategories([]);
                                                }
                                            }}
                                            sx={{
                                                borderColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    backgroundColor: theme[themeStyle].form_divider,
                                                },
                                            }}>
                                            Add Categories
                                        </Button>
                                    </Box>
                                )}
                            </Stack>
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <Stack spacing={3}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Languages (Max 5)
                                </Typography>

                                {/* Selected Languages Chips */}
                                {(formData.languages_spoken || []).length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {(formData.languages_spoken || []).map((language, index) => (
                                            <Chip
                                                key={index}
                                                label={language}
                                                onDelete={() => handleRemoveArrayItem('languages_spoken', language)}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].black,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '& .MuiChip-deleteIcon': {
                                                        color: theme[themeStyle].black,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                <FormControl
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                    }}>
                                    <InputLabel
                                        sx={{
                                            color: theme[themeStyle].form_label,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-focused': { color: theme[themeStyle].highlight },
                                        }}>
                                        Select Languages
                                    </InputLabel>
                                    <Select
                                        multiple
                                        value={formData.languages_spoken || []}
                                        onChange={event => {
                                            const value = event.target.value as string[];
                                            // Limit to 5 selections
                                            const limitedValue = value.slice(0, 5);
                                            handleArrayFieldChange('languages_spoken', limitedValue);
                                        }}
                                        label="Select Languages"
                                        renderValue={selected => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {(selected as string[]).map(value => (
                                                    <Chip
                                                        key={value}
                                                        label={value}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].black,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                    borderWidth: '1px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    borderWidth: '2px',
                                                },
                                            },
                                            '& .MuiSelect-select': {
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].primary_white,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].dropdown_black,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: '8px',
                                                    maxHeight: 300,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].primary_white,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}>
                                        {LANGUAGES.map(language => (
                                            <MenuItem key={language} value={language}>
                                                {language.charAt(0).toUpperCase() + language.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </TabPanel>

                        {/* Preferences Tab */}
                        <TabPanel value={activeTab} index={3}>
                            <Stack spacing={3}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Preferences
                                </Typography>

                                {/* Collaboration Types */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Preferred Collaboration Types
                                    </Typography>
                                    <DropdownSelect
                                        label=""
                                        data={COLLABORATION_TYPES}
                                        placeHolder="Select collaboration type"
                                        onOptionSelected={(value: string) => {
                                            const currentTypes = formData.preferred_collab_types || [];
                                            if (value && !currentTypes.includes(value)) {
                                                handleArrayFieldChange('preferred_collab_types', [
                                                    ...currentTypes,
                                                    value,
                                                ]);
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].lightWhite,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        }}
                                    />
                                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {(formData.preferred_collab_types || []).map((type, index) => (
                                            <Chip
                                                key={index}
                                                label={type}
                                                onDelete={() => handleRemoveArrayItem('preferred_collab_types', type)}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].black,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '& .MuiChip-deleteIcon': {
                                                        color: theme[themeStyle].black,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Availability Switches */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 2,
                                        }}>
                                        Availability
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <CustomizedSwitches
                                                        switchProps={{
                                                            checked: formData.available_for_events || false,
                                                            onChange: e =>
                                                                handleInputChange(
                                                                    'available_for_events',
                                                                    e.target.checked,
                                                                ),
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            color: theme[themeStyle].form_label,
                                                        }}>
                                                        Available for Events
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <CustomizedSwitches
                                                        switchProps={{
                                                            checked: formData.available_for_long_term_collabs || false,
                                                            onChange: e =>
                                                                handleInputChange(
                                                                    'available_for_long_term_collabs',
                                                                    e.target.checked,
                                                                ),
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            color: theme[themeStyle].form_label,
                                                        }}>
                                                        Available for Long-term Collaborations
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <CustomizedSwitches
                                                        switchProps={{
                                                            checked: formData.availability !== false,
                                                            onChange: e =>
                                                                handleInputChange('availability', e.target.checked),
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            color: theme[themeStyle].form_label,
                                                        }}>
                                                        Generally Available
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Preferred Payment Method */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Preferred Payment Method
                                    </Typography>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].form_divider,
                                            },
                                            '&:hover fieldset': { borderColor: theme[themeStyle].highlight },
                                        }}>
                                        <Select
                                            value={formData.preferred_payment_method || ''}
                                            onChange={e =>
                                                handleInputChange('preferred_payment_method', e.target.value)
                                            }
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].form_divider,
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiSelect-select': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme[themeStyle].dropdown_black,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: '8px',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme[themeStyle].primary_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}>
                                            <MenuItem value="">
                                                <Typography sx={{ color: theme[themeStyle].form_label }}>
                                                    Select payment method
                                                </Typography>
                                            </MenuItem>
                                            {PAYMENT_METHODS.map(method => (
                                                <MenuItem key={method} value={method}>
                                                    <Typography sx={{ color: theme[themeStyle].primary_white }}>
                                                        {method.charAt(0).toUpperCase() + method.slice(1)}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Unavailable Dates */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 2,
                                        }}>
                                        Unavailable Dates
                                    </Typography>

                                    {/* Add new unavailable date range */}
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                            borderRadius: 2,
                                            backgroundColor: theme[themeStyle].form_background,
                                        }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].form_label,
                                                        mb: 1,
                                                    }}>
                                                    From Date
                                                </Typography>
                                                <CustomDatePicker
                                                    value={newUnavailableDate.from || ''}
                                                    onChange={(date: string) =>
                                                        setNewUnavailableDate(prev => ({ ...prev, from: date }))
                                                    }
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].form_fields,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].form_label,
                                                        mb: 1,
                                                    }}>
                                                    To Date
                                                </Typography>
                                                <CustomDatePicker
                                                    value={newUnavailableDate.to || ''}
                                                    onChange={(date: string) =>
                                                        setNewUnavailableDate(prev => ({ ...prev, to: date }))
                                                    }
                                                    minDate={newUnavailableDate.from}
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].form_fields,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].form_label,
                                                        mb: 1,
                                                    }}>
                                                    Reason
                                                </Typography>
                                                <TextField
                                                    value={newUnavailableDate.reason || ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        setNewUnavailableDate(prev => ({
                                                            ...prev,
                                                            reason: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="Enter reason for unavailability"
                                                    multiline
                                                    rows={2}
                                                    fullWidth
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].form_fields,
                                                        '& .MuiOutlinedInput-root': {
                                                            color: theme[themeStyle].primary_white,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    onClick={handleAddUnavailableDate}
                                                    disabled={
                                                        !newUnavailableDate.from ||
                                                        !newUnavailableDate.to ||
                                                        !newUnavailableDate.reason
                                                    }
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            opacity: 0.9,
                                                        },
                                                        '&:disabled': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                            color: theme[themeStyle].placeholderGray,
                                                        },
                                                    }}>
                                                    Add Unavailable Date Range
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Display existing unavailable dates */}
                                    {(() => {
                                        const currentDates = (formData.unavailable_dates as any) || [];
                                        const datesArray = Array.isArray(currentDates) ? currentDates : [];
                                        return datesArray.map((dateRange: any, index: number) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    mb: 2,
                                                    p: 2,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: 2,
                                                    backgroundColor: theme[themeStyle].form_background,
                                                }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        mb: 1,
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            color: theme[themeStyle].primary_white,
                                                        }}>
                                                        {new Date(dateRange.from).toLocaleDateString()} -{' '}
                                                        {new Date(dateRange.to).toLocaleDateString()}
                                                    </Typography>
                                                    <Button
                                                        onClick={() => handleRemoveUnavailableDate(index)}
                                                        sx={{
                                                            color: theme[themeStyle].primary_red,
                                                            minWidth: 'auto',
                                                            p: 0,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            textTransform: 'none',
                                                        }}>
                                                        Remove
                                                    </Button>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        color: theme[themeStyle].form_label,
                                                    }}>
                                                    {dateRange.reason}
                                                </Typography>
                                            </Box>
                                        ));
                                    })()}
                                </Box>
                            </Stack>
                        </TabPanel>

                        {/* Media Tab */}
                        <TabPanel value={activeTab} index={4}>
                            <Stack spacing={3}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Media & Documents
                                </Typography>

                                {/* Media Kit Link */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Media Kit Link
                                    </Typography>
                                    <TextField
                                        label="Media Kit URL"
                                        fullWidth
                                        value={formData.media_kit_link || ''}
                                        onChange={e => handleInputChange('media_kit_link', e.target.value)}
                                        placeholder="https://example.com/media-kit"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LinkIcon sx={{ color: theme[themeStyle].form_label }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        {...commonTextFieldProps}
                                    />
                                    {formData.media_kit_link && (
                                        <Box sx={{ mt: 1 }}>
                                            <Link
                                                href={formData.media_kit_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    },
                                                }}>
                                                View Media Kit
                                            </Link>
                                        </Box>
                                    )}
                                </Box>

                                {/* Portfolio Links */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Portfolio Links
                                    </Typography>

                                    {/* Add new portfolio link */}
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <TextField
                                            label="Portfolio URL"
                                            fullWidth
                                            value={newPortfolioLink}
                                            onChange={e => setNewPortfolioLink(e.target.value)}
                                            placeholder="https://thspian.com/username"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LinkIcon sx={{ color: theme[themeStyle].form_label }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...commonTextFieldProps}
                                        />
                                        <Button
                                            onClick={handleAddPortfolioLink}
                                            disabled={!newPortfolioLink.trim()}
                                            variant="outlined"
                                            sx={{
                                                borderColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'none',
                                                minWidth: 'auto',
                                                px: 2,
                                                '&:hover': {
                                                    borderColor: theme[themeStyle].highlight,
                                                    backgroundColor: theme[themeStyle].form_divider,
                                                },
                                                '&:disabled': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                    color: theme[themeStyle].placeholderGray,
                                                },
                                            }}>
                                            <AddIcon />
                                        </Button>
                                    </Box>

                                    {/* Display existing portfolio links */}
                                    {(formData.portfolio || []).length > 0 && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {(formData.portfolio || []).map((link, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 2,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: 1,
                                                        backgroundColor: theme[themeStyle].form_background,
                                                    }}>
                                                    <Link
                                                        href={link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{
                                                            color: theme[themeStyle].highlight,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            textDecoration: 'none',
                                                            flex: 1,
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}>
                                                        {link}
                                                    </Link>
                                                    <IconButton
                                                        onClick={() => handleRemovePortfolioLink(link)}
                                                        size="small"
                                                        sx={{
                                                            color: theme[themeStyle].primary_red,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                        }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                                {/* Documents Upload */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].form_label,
                                            mb: 1,
                                        }}>
                                        Documents
                                    </Typography>

                                    {/* File upload input */}
                                    <Box sx={{ mb: 2 }}>
                                        <input
                                            type="file"
                                            multiple
                                            onChange={handleDocumentUpload}
                                            style={{ display: 'none' }}
                                            id="document-upload"
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                                        />
                                        <label htmlFor="document-upload">
                                            <Button
                                                component="span"
                                                variant="outlined"
                                                sx={{
                                                    borderColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].highlight,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        backgroundColor: theme[themeStyle].form_divider,
                                                    },
                                                }}>
                                                <AddIcon sx={{ mr: 1 }} />
                                                Upload Documents
                                            </Button>
                                        </label>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                mt: 1,
                                                color: theme[themeStyle].placeholderGray,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            }}>
                                            Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF
                                        </Typography>
                                    </Box>

                                    {/* Display uploaded documents */}
                                    {uploadedDocuments.length > 0 && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {uploadedDocuments.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        p: 2,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                        borderRadius: 1,
                                                        mb: 1,
                                                    }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <LinkIcon sx={{ color: theme[themeStyle].form_label }} />
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: theme[themeStyle].primary_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                }}>
                                                                {file.name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                }}>
                                                                {file.size
                                                                    ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                                                    : file.format || 'Unknown type'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        onClick={() => handleRemoveDocument(index)}
                                                        sx={{
                                                            color: theme[themeStyle].primary_red,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                        }}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Display existing documents */}
                                    {(formData.documents || []).length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].form_label,
                                                    mb: 1,
                                                }}>
                                                Existing Documents
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {(formData.documents || []).map((doc, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            p: 2,
                                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                                            borderRadius: 1,
                                                            backgroundColor: theme[themeStyle].form_background,
                                                        }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].primary_white,
                                                                    mb: 0.5,
                                                                }}>
                                                                {doc.name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: theme[themeStyle].placeholderGray,
                                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                }}>
                                                                {doc.format}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Link
                                                                href={doc.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: theme[themeStyle].highlight,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    textDecoration: 'none',
                                                                    '&:hover': {
                                                                        textDecoration: 'underline',
                                                                    },
                                                                }}>
                                                                View
                                                            </Link>
                                                            <Button
                                                                onClick={() => {
                                                                    const currentDocs = formData.documents || [];
                                                                    handleInputChange(
                                                                        'documents',
                                                                        currentDocs.filter((_, i) => i !== index),
                                                                    );
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    color: theme[themeStyle].primary_red,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    textTransform: 'none',
                                                                    minWidth: 'auto',
                                                                    p: 0,
                                                                }}>
                                                                Remove
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>

                                <EditProfileMediaExtras
                                    projects={portfolioProjects}
                                    onProjectsChange={setPortfolioProjects}
                                    galleryUrls={galleryUrls}
                                    onGalleryUrlsChange={setGalleryUrls}
                                    pendingGalleryUploads={pendingGalleryUploads}
                                    onPendingGalleryUploadsChange={setPendingGalleryUploads}
                                    themeTokens={theme[themeStyle]}
                                    themeStyle={themeStyle}
                                />
                            </Stack>
                        </TabPanel>

                        <TabPanel value={activeTab} index={5}>
                            <Stack spacing={3}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].primary_white,
                                        fontSize: 12,
                                        opacity: 0.85,
                                    }}>
                                    Adjusting synced social metrics is restricted; contact admin for changes. Set your
                                    content pricing on the Rate Card tab.
                                </Typography>

                                <EditProfileConnectedPlatformCards
                                    formData={formData}
                                    themeTokens={theme[themeStyle]}
                                    themeStyle={themeStyle}
                                    onManualSave={onSave}
                                    onPlatformConnected={onPlatformConnected}
                                />

                                <Accordion
                                    defaultExpanded={false}
                                    sx={{
                                        backgroundColor:
                                            themeStyle === 'dark' ? '#131313' : theme[themeStyle].secondaryBg,
                                        borderRadius: 2,
                                        '&:before': { display: 'none' },
                                    }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].form_label }} />}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            Facebook & Twitter (account details)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0 }}>
                                        {/* Facebook */}
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].highlight,
                                                    mb: 2,
                                                }}>
                                                Facebook
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Username"
                                                        fullWidth
                                                        value={formData.facebook?.username || ''}
                                                        disabled
                                                        onChange={e =>
                                                            handleInputChange('facebook', {
                                                                ...formData.facebook,
                                                                username: e.target.value,
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Followers"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.facebook?.followers || ''}
                                                        onChange={e =>
                                                            handleInputChange('facebook', {
                                                                ...formData.facebook,
                                                                followers: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Engagement Rate"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.facebook?.engagementRate || ''}
                                                        onChange={e =>
                                                            handleInputChange('facebook', {
                                                                ...formData.facebook,
                                                                engagementRate: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.5,
                                                                        }}>
                                                                        <Typography
                                                                            sx={{
                                                                                color: theme[themeStyle]
                                                                                    .placeholderGray,
                                                                                fontSize: 14,
                                                                            }}>
                                                                            %
                                                                        </Typography>
                                                                        <LockIcon
                                                                            sx={{
                                                                                color: theme[themeStyle]
                                                                                    .placeholderGray,
                                                                                fontSize: 16,
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Average Likes"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.facebook?.avg_likes || ''}
                                                        onChange={e =>
                                                            handleInputChange('facebook', {
                                                                ...formData.facebook,
                                                                avg_likes: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Average Comments"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.facebook?.avg_comments || ''}
                                                        onChange={e =>
                                                            handleInputChange('facebook', {
                                                                ...formData.facebook,
                                                                avg_comments: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Profile Link"
                                                        fullWidth
                                                        disabled
                                                        value={formData.facebook?.profile_link || ''}
                                                        onChange={e =>
                                                            handleInputChange('facebook', {
                                                                ...formData.facebook,
                                                                profile_link: e.target.value,
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {/* Twitter */}
                                        <Box sx={{ mt: 4 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].highlight,
                                                    mb: 2,
                                                }}>
                                                Twitter
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Username"
                                                        fullWidth
                                                        value={formData.twitter?.username || ''}
                                                        disabled
                                                        onChange={e =>
                                                            handleInputChange('twitter', {
                                                                ...formData.twitter,
                                                                username: e.target.value,
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Followers"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.twitter?.followers || ''}
                                                        onChange={e =>
                                                            handleInputChange('twitter', {
                                                                ...formData.twitter,
                                                                followers: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Engagement Rate"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.twitter?.engagementRate || ''}
                                                        onChange={e =>
                                                            handleInputChange('twitter', {
                                                                ...formData.twitter,
                                                                engagementRate: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.5,
                                                                        }}>
                                                                        <Typography
                                                                            sx={{
                                                                                color: theme[themeStyle]
                                                                                    .placeholderGray,
                                                                                fontSize: 14,
                                                                            }}>
                                                                            %
                                                                        </Typography>
                                                                        <LockIcon
                                                                            sx={{
                                                                                color: theme[themeStyle]
                                                                                    .placeholderGray,
                                                                                fontSize: 16,
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Average Likes"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.twitter?.avg_likes || ''}
                                                        onChange={e =>
                                                            handleInputChange('twitter', {
                                                                ...formData.twitter,
                                                                avg_likes: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Average Comments"
                                                        fullWidth
                                                        disabled
                                                        type="number"
                                                        value={formData.twitter?.avg_comments || ''}
                                                        onChange={e =>
                                                            handleInputChange('twitter', {
                                                                ...formData.twitter,
                                                                avg_comments: Number(e.target.value),
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Profile Link"
                                                        fullWidth
                                                        disabled
                                                        value={formData.twitter?.profile_link || ''}
                                                        onChange={e =>
                                                            handleInputChange('twitter', {
                                                                ...formData.twitter,
                                                                profile_link: e.target.value,
                                                            })
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LockIcon
                                                                        sx={{
                                                                            color: theme[themeStyle].placeholderGray,
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            ...commonTextFieldProps.sx,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                                WebkitTextFillColor: theme[themeStyle].placeholderGray,
                                                                opacity: 0.5,
                                                            },
                                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                                color: theme[themeStyle].placeholderGray,
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                                                                {
                                                                    borderColor: theme[themeStyle].form_divider,
                                                                },
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Stack>
                        </TabPanel>

                        <TabPanel value={activeTab} index={6}>
                            <EditProfileRateCardTab
                                formData={formData}
                                handleInputChange={handleInputChange}
                                themeTokens={theme[themeStyle]}
                                themeStyle={themeStyle}
                            />
                        </TabPanel>

                        <TabPanel value={activeTab} index={7}>
                            <EditProfileConfigTab
                                hideRateCard={Boolean(formData.hide_rate_card)}
                                onHideRateCardChange={value => handleInputChange('hide_rate_card', value)}
                                themeTokens={theme[themeStyle]}
                                themeStyle={themeStyle}
                            />
                        </TabPanel>
                    </Box>
                </Box>
            </DialogContent>

            <Box
                sx={{
                    display: { xs: 'block', md: 'none' },
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: muiTheme.zIndex.modal + 2,
                    p: 2,
                    pt: 1.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    backgroundColor: themeStyle === 'dark' ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(12px)',
                }}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSave}
                    disabled={!hasAnyChanges()}
                    sx={{
                        py: 1.75,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 700,
                        background: `linear-gradient(90deg, ${theme[themeStyle].highlight}, #feb700)`,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                        color: 'rgba(26, 18, 0, 0.95)',
                        '&:hover': {
                            opacity: 0.92,
                            background: `linear-gradient(90deg, ${theme[themeStyle].highlight}, #feb700)`,
                        },
                    }}>
                    Save All Changes
                </Button>
            </Box>

            <DialogActions
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${
                        themeStyle === 'dark' ? 'rgba(72,72,71,0.45)' : theme[themeStyle].form_divider
                    }`,
                    bgcolor: themeStyle === 'dark' ? '#1a1919' : 'transparent',
                    px: 2,
                    py: 1.5,
                    gap: 1,
                }}>
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 700,
                        fontSize: 12,
                        color: theme[themeStyle].form_label,
                        '&:hover': { color: theme[themeStyle].primary_white, bgcolor: 'transparent' },
                    }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!hasAnyChanges()}
                    sx={{
                        px: 2.5,
                        py: 1.25,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 700,
                        fontSize: 12,
                        bgcolor: '#ffc965',
                        color: '#392700',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                        '&:hover': { bgcolor: '#ecaa00' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.35)', color: 'rgba(57,39,0,0.55)' },
                    }}>
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};
